import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  School as SchoolIcon,
  EmojiEvents as TrophyIcon,
  Assignment as AssignmentIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  CompareArrows as CompareArrowsIcon
} from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import StudentTrendChart from '../../components/teacher/StudentTrendChart';
import ContactStudentDialog from '../../components/teacher/ContactStudentDialog';
import StudentAttemptMetrics from '../../components/teacher/StudentAttemptMetrics';
import PerProblemAttemptTable from '../../components/teacher/PerProblemAttemptTable';
import StudentComparisonDialog from '../../components/teacher/StudentComparisonDialog';
import { getStudent, getStudentProgress, getStudentAttemptAnalytics, getStudents } from '../../services/teacherService';
import { getRankedProfile, getRatingHistory, getQuizResults } from '../../services/rankedService';
import { Student, StudentProgress, StudentAttemptAnalytics } from '../../types/teacher';
import { QuizResultRow, RankedProfile, RatingHistoryEvent } from '../../types/ranked';
import RankedProfilePanel from '../../components/ranked/RankedProfilePanel';
import QuizResultsTable from '../../components/ranked/QuizResultsTable';

function StudentDetailPage() {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();

  const [student, setStudent] = useState<Student | null>(null);
  const [progress, setProgress] = useState<StudentProgress | null>(null);
  const [analytics, setAnalytics] = useState<StudentAttemptAnalytics | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);
  const [refreshTrigger] = useState(0);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [rankedProfile, setRankedProfile] = useState<RankedProfile | null>(null);
  const [ratingHistory, setRatingHistory] = useState<RatingHistoryEvent[]>([]);
  const [quizResults, setQuizResults] = useState<QuizResultRow[]>([]);
  const [rankedLoading, setRankedLoading] = useState<boolean>(true);
  const [selectedComparisonStudentId, setSelectedComparisonStudentId] = useState<number | ''>('');

  const fetchStudentData = async () => {
    if (!studentId) return;

    try {
      setLoading(true);
      setError(null);

      const [studentData, progressData, analyticsData] = await Promise.all([
        getStudent(parseInt(studentId)),
        getStudentProgress(parseInt(studentId)),
        getStudentAttemptAnalytics(parseInt(studentId))
      ]);

      setStudent(studentData);
      setProgress(progressData);
      setAnalytics(analyticsData);

      // Ranked data is fetched separately and its failure swallowed: a season
      // with no ranked activity must not blank the problem-set analytics a
      // teacher came here for.
      setRankedLoading(true);
      try {
        const [profileData, historyData, resultsData] = await Promise.all([
          getRankedProfile(parseInt(studentId)),
          getRatingHistory(parseInt(studentId)),
          getQuizResults(parseInt(studentId))
        ]);
        setRankedProfile(profileData);
        setRatingHistory(historyData);
        setQuizResults(resultsData);
      } catch (err) {
        console.error('Error fetching ranked data:', err);
      } finally {
        setRankedLoading(false);
      }
    } catch (err) {
      console.error('Error fetching student data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load student data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studentId) {
      fetchStudentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  useEffect(() => {
    const fetchAllStudents = async () => {
      try {
        const students = await getStudents();
        setAllStudents(students);
      } catch (err) {
        console.error('Error fetching students for comparison:', err);
      }
    };
    fetchAllStudents();
  }, []);

  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 80) return 'success';
    if (accuracy >= 60) return 'warning';
    return 'error';
  };

  const getAccuracyIcon = (accuracy: number) => {
    if (accuracy >= 60) return <TrendingUpIcon />;
    return <TrendingDownIcon />;
  };

  const handleCompareStudents = () => {
    if (typeof selectedComparisonStudentId === 'number') {
      setComparisonDialogOpen(true);
    }
  };

  const getSelectedComparisonStudent = () => {
    if (typeof selectedComparisonStudentId !== 'number') return null;
    return allStudents.find(s => s.id === selectedComparisonStudentId);
  };

  if (loading) {
    return (
      <MainLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={48} />
        </Box>
      </MainLayout>
    );
  }

  if (error || !student || !progress) {
    return (
      <MainLayout>
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/teacher/dashboard')}
            sx={{ mb: 2 }}
          >
            Back to Dashboard
          </Button>
          <Alert severity="error">
            {error || 'Student not found'}
          </Alert>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Header with Back Button */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/teacher/dashboard')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>

        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'primary.main',
              fontSize: '1.5rem'
            }}
          >
            {student.firstName?.charAt(0)}{student.lastName?.charAt(0)}
          </Avatar>
          <Box flexGrow={1}>
            <Typography variant="h4" fontWeight="600" gutterBottom>
              {student.firstName} {student.lastName}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip label={student.email} variant="outlined" size="small" />
              {progress.grade && <Chip label={`Grade ${progress.grade}`} size="small" />}
              {progress.school && <Chip label={progress.school} size="small" variant="outlined" />}
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<EmailIcon />}
            onClick={() => setContactDialogOpen(true)}
          >
            Contact Student
          </Button>
        </Box>
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Performance Summary Cards */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  bgcolor: (theme) => `${theme.palette.primary.main}15`,
                  borderLeft: 4,
                  borderColor: 'primary.main'
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <AssignmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color="primary.main">
                      {progress.totalSubmissions}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Problems Solved
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  bgcolor: (theme) => `${theme.palette.secondary.main}15`,
                  borderLeft: 4,
                  borderColor: 'secondary.main'
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  {getAccuracyIcon(progress.accuracyPercentage)}
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color="secondary.main">
                      {progress.accuracyPercentage.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Accuracy Rate
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  bgcolor: (theme) => `${theme.palette.warning.main}15`,
                  borderLeft: 4,
                  borderColor: 'warning.main'
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <SchoolIcon sx={{ fontSize: 40, color: 'warning.main' }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color="warning.main">
                      {progress.totalPoints.toFixed(0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Points
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  bgcolor: (theme) => `${theme.palette.accent.main}15`,
                  borderLeft: 4,
                  borderColor: 'accent.main'
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <TrophyIcon sx={{ fontSize: 40, color: 'accent.main' }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color="accent.main">
                      {progress.achievementsEarned}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Achievements
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Ranked standing and quiz results. Third-person voice — a teacher
            reading about a student, not a student reading about themselves. */}
        {rankedProfile && (
          <Grid item xs={12} md={8}>
            <Card elevation={2} sx={{ mb: 3 }}>
              <CardHeader title="Ranked Mathlete" />
              <Divider />
              <CardContent>
                <RankedProfilePanel
                  profile={rankedProfile}
                  latestDelta={ratingHistory[0]?.delta ?? null}
                  voice="third"
                  subjectName={student.firstName}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={8}>
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardHeader title="Quiz Results" />
            <Divider />
            <CardContent>
              {/* Live and self-paced together. A live quiz with too small a
                  field shows as "Didn't count" rather than vanishing. */}
              <QuizResultsTable
                results={quizResults}
                loading={rankedLoading}
                emptyMessage={`${student.firstName} hasn't taken any quizzes yet.`}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Detailed Metrics */}
        <Grid item xs={12} md={8}>
          <Card elevation={2}>
            <CardHeader title="Performance Details" />
            <Divider />
            <CardContent>
              {/* Accuracy Breakdown */}
              <Box sx={{ mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight="600">
                    Correct Submissions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {progress.correctSubmissions} / {progress.totalSubmissions}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(progress.correctSubmissions / progress.totalSubmissions) * 100}
                  color={getAccuracyColor(progress.accuracyPercentage)}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>

              {/* Enrollment Status */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                  Enrollment Status
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`${progress.activeEnrollments} Active`}
                    color="success"
                    variant="filled"
                  />
                  <Chip
                    label={`${progress.totalEnrollments} Total Enrollments`}
                    variant="outlined"
                  />
                </Box>
                {progress.totalEnrollments > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(progress.activeEnrollments / progress.totalEnrollments) * 100}
                      color="success"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                )}
              </Box>

              {/* Last Activity */}
              {progress.lastSubmissionDate && (
                <Box>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Last Activity
                  </Typography>
                  <Typography variant="body1">
                    {new Date(progress.lastSubmissionDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
              )}

              {/* Performance Insights */}
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                  Performance Insights
                </Typography>
                {progress.accuracyPercentage >= 80 && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    This student is performing excellently and demonstrates strong mastery of the material.
                  </Alert>
                )}
                {progress.accuracyPercentage >= 60 && progress.accuracyPercentage < 80 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    This student shows good understanding but may benefit from additional practice on challenging topics.
                  </Alert>
                )}
                {progress.accuracyPercentage < 60 && progress.totalSubmissions > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    This student may need additional support. Consider scheduling a one-on-one session or providing targeted practice materials.
                  </Alert>
                )}
                {progress.totalSubmissions === 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    This student hasn't started any work yet. Consider reaching out to encourage participation.
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Trend Chart */}
          <Card elevation={2} sx={{ mt: 3 }}>
            <CardHeader title="Performance Trends" />
            <Divider />
            <CardContent>
              <StudentTrendChart
                studentId={student.id}
                studentName={`${student.firstName} ${student.lastName}`}
                refreshTrigger={refreshTrigger}
              />
            </CardContent>
          </Card>

          {/* Attempt Analytics */}
          <Box sx={{ mt: 3 }}>
            <StudentAttemptMetrics
              studentId={student.id}
              studentName={`${student.firstName} ${student.lastName}`}
              refreshTrigger={refreshTrigger}
            />
          </Box>

          {/* Per-Problem Breakdown */}
          {analytics && analytics.problemBreakdown && analytics.problemBreakdown.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <PerProblemAttemptTable
                problemBreakdown={analytics.problemBreakdown}
                studentName={`${student.firstName} ${student.lastName}`}
              />
            </Box>
          )}
        </Grid>

        {/* Quick Actions Sidebar */}
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardHeader title="Quick Actions" />
            <Divider />
            <CardContent>
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<EmailIcon />}
                  onClick={() => setContactDialogOpen(true)}
                >
                  Send Message
                </Button>
                <Button variant="outlined" fullWidth disabled>
                  View Submissions
                </Button>
                <Button variant="outlined" fullWidth disabled>
                  Assign Practice
                </Button>
                <Button variant="outlined" fullWidth disabled>
                  Schedule Meeting
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Student Comparison Card */}
          <Card elevation={2} sx={{ mt: 3 }}>
            <CardHeader title="Compare Students" />
            <Divider />
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Compare this student's performance with another student to understand scoring differences.
              </Typography>
              <Box display="flex" flexDirection="column" gap={2} mt={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Student</InputLabel>
                  <Select
                    value={selectedComparisonStudentId}
                    onChange={(e) => setSelectedComparisonStudentId(e.target.value as number)}
                    label="Select Student"
                  >
                    <MenuItem value="">
                      <em>Select a student...</em>
                    </MenuItem>
                    {allStudents
                      .filter(s => s.id !== parseInt(studentId || '0'))
                      .map(s => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.firstName} {s.lastName}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<CompareArrowsIcon />}
                  onClick={handleCompareStudents}
                  disabled={!selectedComparisonStudentId}
                >
                  Compare Performance
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Student Info Card */}
          <Card elevation={2} sx={{ mt: 3 }}>
            <CardHeader title="Student Information" />
            <Divider />
            <CardContent>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2">{student.email}</Typography>
                </Box>
                {progress.grade && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Grade Level
                    </Typography>
                    <Typography variant="body2">Grade {progress.grade}</Typography>
                  </Box>
                )}
                {progress.school && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      School
                    </Typography>
                    <Typography variant="body2">{progress.school}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Student ID
                  </Typography>
                  <Typography variant="body2">{student.id}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Contact Dialog */}
      <ContactStudentDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        studentName={`${student.firstName} ${student.lastName}`}
        studentId={student.id}
      />

      {/* Comparison Dialog */}
      {selectedComparisonStudentId && getSelectedComparisonStudent() && (
        <StudentComparisonDialog
          open={comparisonDialogOpen}
          onClose={() => setComparisonDialogOpen(false)}
          student1Id={student.id}
          student2Id={selectedComparisonStudentId as number}
          student1Name={`${student.firstName} ${student.lastName}`}
          student2Name={`${getSelectedComparisonStudent()?.firstName} ${getSelectedComparisonStudent()?.lastName}`}
        />
      )}
    </MainLayout>
  );
}

export default StudentDetailPage;
